import type { Messages } from '../catalogue.en'

/**
 * Correspondence, the patient's message register and communication
 * preferences, in Hebrew.
 *
 * The delivery words keep the English module's distinctions exactly: «התקבל
 * אצל הספק» is the provider taking the message and «נמסר» is the recipient
 * having it, and no Hebrew word is allowed to stand for both.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const correspondence: Partial<Messages> = {
  'correspondence.refusal.sourceInactive': 'האירוע נסגר או שהנמען השתנה לפני השליחה.',
  'correspondence.refusal.protectedPayloadUnavailable': 'לא ניתן להכין את הקישור המוגן למסמך. יש לפנות למנהל המרפאה.',
  'correspondence.listLabel': 'התכתבות עם מטופל זה',
  'correspondence.empty': 'עדיין אין התכתבות',
  'correspondence.emptyDetail':
    'הודעות שהוחלפו עם מטופל זה בדוא"ל, ב-Telegram או ב-WhatsApp יופיעו כאן.',
  'correspondence.truncated':
    'המרפאה שומרת במפתח זה רק את {count} השיחות החדשות ביותר, ולכן ישנות יותר עשויות להיות חסרות כאן.',
  'correspondence.refresh': 'רענון',

  'correspondence.direction.inbound': 'התקבל',
  'correspondence.direction.outbound': 'נשלח',
  'correspondence.actorKind.provider': 'דרך הספק',
  'correspondence.actorKind.service': 'אוטומטי',

  'correspondence.channel.email': 'דוא"ל',
  'correspondence.channel.telegram': 'Telegram',
  'correspondence.channel.whatsapp': 'WhatsApp',
  'correspondence.channel.inApp': 'בתוך היישום',

  'correspondence.to': 'אל {name} בכתובת {address}',
  'correspondence.from': 'מאת {name} מהכתובת {address}',
  'correspondence.endpointStatus': 'מצב הכתובת בעת הרישום: {status}',
  'correspondence.purpose': 'ייעוד: {purpose}',
  'correspondence.noSubject': 'ללא נושא',
  'correspondence.mediaOnly': 'נשא קובץ מצורף ללא טקסט. קבצים מצורפים אינם מוצגים כאן.',
  'correspondence.webAppButton': 'הוצע הכפתור „{text}“',

  'correspondence.state.accepted': 'נשלח',
  'correspondence.state.delivered': 'נמסר',
  'correspondence.state.undelivered': 'לא נמסר',
  'correspondence.state.read': 'נקרא',
  'correspondence.state.received': 'התקבל',
  'correspondence.retrying': 'ניסיון חוזר לאחר כישלון',

  'correspondence.refusal.connectorMismatch': 'החיבור לשליחת הודעות מוגדר באופן שגוי. יש לפנות למנהל המרפאה.',
  'correspondence.refusal.endpointAmbiguous': 'מוגדרות כמה כתובות לנמען. יש לבקש ממנהל המרפאה לבדוק את פרטי הקשר.',
  'correspondence.refusal.serviceUnavailable': 'שירות ההודעות אינו זמין. המסירה לא אושרה.',
  'correspondence.refusal.telegramRejected': 'טלגרם סירב לשלוח את ההודעה. יש לבדוק שהמטופל הפעיל את הבוט ולא חסם אותו.',
  'correspondence.refusal.messageInvalid': 'לא ניתן לשלוח הודעה זו בערוץ שנבחר. יש לבדוק את אורכה ואת תוכנה.',
  'correspondence.refusal.unknown': 'הערוץ לא הצליח לשלוח את ההודעה. יש לפנות למנהל המרפאה.',
  'correspondence.refusal.noSelectedChannels': 'לא נבחר ערוץ תקשורת לנמען.',
  'correspondence.refusal.routeMissing': 'לא הוגדר נתיב לערוץ זה',
  'correspondence.refusal.routeAmbiguous': 'הוגדר יותר מנתיב אחד לערוץ זה',
  'correspondence.refusal.routeChanged': 'הנתיב השתנה בזמן רישום ההודעה',
  'correspondence.refusal.endpointUnverified': 'הכתובת לא אומתה',
  'correspondence.refusal.preferenceRequired': 'המטופל לא בחר בערוץ זה',
  'correspondence.refusal.preferenceDenied': 'המטופל סירב לערוץ זה',
  'correspondence.refusal.connectorUnavailable': 'המחבר לא היה זמין',
  'correspondence.refused': 'נדחה: {reason}',
  'correspondence.refusedCode': 'נדחה עם הקוד {code}',

  'correspondence.historyShow': 'היסטוריית מסירה',
  'correspondence.historyHide': 'הסתרת היסטוריית המסירה',
  'correspondence.historyLabel': 'היסטוריית המסירה של ההודעה מ-{time}',
  'correspondence.historyEmpty': 'הספק עדיין לא דיווח דבר על הודעה זו.',
  'correspondence.historyRefused': 'אין לך הרשאה לצפות בהיסטוריית המסירה.',
  'correspondence.historyFailed': 'לא ניתן לטעון את היסטוריית המסירה.',
  'correspondence.attempt': 'ניסיון {n}',
  'correspondence.attemptUnknown': 'דיווח הספק',
  'correspondence.eventSimulated': 'הדמיה',
  'correspondence.eventDetail': '{state} — {detail}',

  'correspondence.provenance.whatsAppProvisional': 'הזהות ב-WhatsApp טרם אושרה',
  'correspondence.provenance.whatsAppVerified': 'הזהות ב-WhatsApp אושרה',
  'correspondence.provenance.lead': 'התחיל כפנייה לפני שהתיק הזה נוצר',

  'correspondence.compose': 'כתיבת הודעה',
  'correspondence.reply': 'תשובה',
  'correspondence.replyTitle': 'תשובה דרך {channel}',
  'correspondence.replyingTo': 'תשובה בשיחת {channel} להודעה מ-{time}.',
  'correspondence.replyCancel': 'כתיבת הודעה חדשה במקום',
  'correspondence.channelLabel': 'ערוץ',
  'correspondence.channelHint':
    'מוצעים רק ערוצים עם כתובת מאומתת, בחירת המטופל ונתיב פעיל.',
  'correspondence.channelWithheld': '{channel}: {reason}',
  'correspondence.withheld.noPermission': 'אין לך אפשרות לשלוח בערוץ זה',
  'correspondence.withheld.noEndpoint': 'אין כתובת מאומתת',
  'correspondence.withheld.notAllowed': 'המטופל לא בחר בערוץ זה',
  'correspondence.withheld.denied': 'המטופל סירב',
  'correspondence.withheld.noRoute': 'אין נתיב פעיל',
  'correspondence.withheld.unknown': 'לא ניתן היה לבדוק את הכתובת ואת בחירת המטופל',
  'correspondence.noChannel': 'כרגע אין ערוץ זמין למטופל זה.',
  'correspondence.needChannel': 'בחרו ערוץ לפני השליחה.',
  'correspondence.chatSubjectNote': 'ב-Telegram וב-WhatsApp הנושא נשלח כשורה הראשונה.',
  'correspondence.send': 'שליחה',
  'correspondence.sending': 'שולח…',
  'correspondence.recorded': 'נרשם והועבר אל {channel}.',
  'correspondence.recordedRefused': 'נרשם, אך המסירה נדחתה: {reason}.',
  'correspondence.sendMismatch':
    'שיחה זו כבר אינה תואמת לערוץ שנבחר. הטקסט נשמר — בחרו את הערוץ מחדש.',
  'correspondence.sendStale': 'הערוץ כבר אינו זמין. הטקסט נשמר — בחרו ערוץ אחר.',
  'correspondence.sendEndpointGone': 'כתובת זו כבר אינה זמינה. הטקסט נשמר — בחרו ערוץ אחר.',
  /* Correspondence */
  'message.sectionTitle': 'הודעות',
  'message.tableLabel': 'הודעות שנשלחו למטופל זה',
  'message.readRefused': 'אין לך הרשאה לצפות בהודעות של מטופל זה.',
  'message.readFailed': 'לא ניתן היה לטעון את ההודעות.',
  'message.empty': 'עדיין לא נשלח דבר',
  'message.emptyDetail': 'הודעות שייכתבו למטופל זה יופיעו כאן.',
  'message.column.subject': 'נושא',
  'message.column.author': 'נכתב על ידי',
  'message.column.sent': 'נשלח',
  'message.column.state': 'מסירה',
  'message.column.addressedTo': 'ממוען אל {address}',
  'message.composerTitle': 'כתיבת הודעה',
  'message.recipientLabel': 'נמען',
  'message.recipientPlaceholder': 'לא נבחר נמען',
  'message.patientLabel': 'מטופל',
  'message.recipientAllGroup': 'כל הנמענים',
  'message.recipientNoMatches': 'לא נמצא נמען מתאים',
  'message.recipientLoadFailed': 'לא ניתן היה לטעון את הנמענים.',
  'message.noRecipientChosen': 'בחרו למי מיועדת ההודעה.',
  'message.noPatientChosenFiles': 'בחרו למי מיועדים הקבצים.',
  'message.badgeNoAddress': 'אין כתובת דוא״ל רשומה',
  'message.subjectLabel': 'נושא',
  'message.bodyLabel': 'הודעה',
  'message.signatureTitle': 'החתימה שלך',
  'message.signatureEmpty': 'לא הכנת חתימה. ההודעה תישלח בלעדיה.',
  'message.signatureEdit': 'עריכה בפרופיל שלך',
  'message.send': 'שליחה',
  'message.sending': 'שולח…',
  'message.needRecipient': 'בחרו נמען לפני השליחה.',
  'message.needSubject': 'כתבו נושא לפני השליחה.',
  'message.needBody': 'כתבו את ההודעה לפני השליחה.',
  'message.needPatientForFiles': 'בחרו מטופל לפני הוספת הקבצים.',
  'message.sendNoAddress':
    'לנמען זה אין כתובת דוא״ל רשומה, ולכן אין לאן לשלוח.',
  'message.sendInvalid': 'ההודעה נדחתה: בדקו את הנושא ואת הטקסט.',
  'message.sendTooLong': 'ההודעה ארוכה מדי לשליחה.',
  'message.sendConflict': 'הודעה זו כבר נשלחה. ערכו אותה כדי לשלוח הודעה אחרת.',
  'message.sendForbidden': 'אין לך הרשאה לשלוח הודעות.',
  'message.sendRecipientGone': 'הנמען אינו זמין עוד.',
  'message.sendSessionEnded': 'החיבור הסתיים. היכנסו שוב כדי לשלוח.',
  'message.sendUnavailable': 'לא ניתן היה לשלוח את ההודעה. נסו שוב.',
  'message.sendFailed': 'לא ניתן היה לשלוח את ההודעה.',
  'message.state.partial': 'תוצאות המסירה שונות בין הערוצים',
  'message.noSelectedChannels': 'ההודעה לא נשלחה: אין למטופל ערוץ תקשורת זמין.',
  'message.internalFallback':
    'המסירה בערוץ החיצוני נכשלה. ההודעה זמינה לנמען בהתראות הפנימיות.',
  'message.channelOutcome': '{channel}: {state}',
  'message.state.pending': 'בשליחה…',
  'message.state.sent': 'נשלח',
  'message.state.simulated': 'לא נמסר — הדגמה',
  'message.state.failed': 'לא נשלח',
  'message.outcomeUnavailable':
    'לא ניתן לעדכן את מצב המסירה. יש לבדוק את החיבור ולא לשלוח עותק נוסף בינתיים.',
  'message.outcomeAddressed': 'ממוען אל {name} בכתובת {address}.',
  'message.discardTitle': 'למחוק את ההודעה?',
  'message.discardDetail': 'מה שכתבת לא יישמר.',
  'message.keepEditing': 'המשך כתיבה',
  'message.discardConfirm': 'מחיקה',
  'message.unsavedIntent': 'יש הודעה שלא נשלחה.',

  'commPrefs.title': 'העדפות תקשורת',
  'commPrefs.readRefused': 'אין לך הרשאה לצפות בהעדפות התקשורת של אדם זה.',
  'commPrefs.readFailed': 'לא ניתן לטעון את העדפות התקשורת.',
  'commPrefs.edit': 'עריכת העדפות התקשורת',
  'commPrefs.readOnly': 'באפשרותך לצפות בהעדפות אלה אך לא לשנותן.',
  'commPrefs.endpointsTitle': 'כתובות מאומתות',
  'commPrefs.endpoint.status.provisional': 'ממתין לאימות',
  'commPrefs.endpoint.status.verified': 'מאומת',
  'commPrefs.endpoint.status.revoked': 'בוטל',

  'commPrefs.purpose.patientCommunications': 'צ׳אט עם המרפאה',
  'commPrefs.purpose.patientMessage': 'הודעות מהמרפאה',
  'commPrefs.purpose.treatmentCostEstimate': 'הערכות עלות טיפול',
  'commPrefs.purpose.onboardingVerification': 'אימות בקליטה',
  'commPrefs.purpose.staffCommunications': 'הודעות שירות של המרפאה',
  'commPrefs.purpose.taskReminder': 'תזכורות למשימות',

  'commPrefs.messageTypes': 'הודעות',
  'commPrefs.blockMessages': 'חסימת שליחת הודעות',
  'commPrefs.messagesBlocked': 'שליחת ההודעות חסומה.',
  'commPrefs.unselectedNote':
    'כל סוג הודעה שנבחר נשלח בכל ערוץ שנבחר; כל מה שלא סומן — סירוב.',
  'commPrefs.channels': 'ערוצים',
  'commPrefs.channelLinkNote': 'למסירה נדרשים גם כתובת מאומתת או יישומון הודעות מקושר.',
  'commPrefs.hours.title': 'שעות ההודעות',
  'commPrefs.hours.detail':
    'הודעות רגילות, בכל יום, {zone}. הטווח יכול לחצות חצות. אירועים דחופים נשלחים מיד בכל מקרה.',
  'commPrefs.hours.always': 'בכל שעה',
  'commPrefs.hours.from': 'משעה',
  'commPrefs.hours.until': 'עד שעה',
  'commPrefs.hours.window': 'מ־{from} עד {until}',
  'commPrefs.hours.invalid': 'יש לבחור שעות התחלה וסיום שונות.',
  'commPrefs.incompleteSelection':
    'יש לבחור לפחות סוג הודעה אחד וערוץ אחד, או להסיר את כל הסימונים בשתי הקבוצות כדי לסרב לכל ההודעות שאינן חובה.',

  'commPrefs.saveSettings': 'שמירת ההעדפות',
  'commPrefs.saving': 'שומר…',
  'commPrefs.cancel': 'ביטול',
  'commPrefs.saved': 'נשמר.',
  'commPrefs.batchFailed': 'לא ניתן לשמור את ההעדפות. יש לבדוק את ההעדפות השמורות לפני ניסיון נוסף.',
  'commPrefs.batchConflict':
    'ההעדפות שונו במקום אחר, ולכן שמירה זו נדחתה. יש לטעון את ההעדפות השמורות לפני בחירה חדשה.',
  'commPrefs.saveForbidden': 'אין לך הרשאה לשנות העדפות תקשורת.',
  'commPrefs.saveInvalid': 'ההעדפות נדחו: בדקו את השדות.',
  'commPrefs.saveSessionEnded': 'ההפעלה הסתיימה. יש להיכנס שוב כדי לשמור.',
  'commPrefs.reloadSettings': 'טעינת ההעדפות השמורות',
  'commPrefs.reloadFailed': 'לא ניתן לטעון את ההעדפות. הטיוטה נשמרה.',
  'commPrefs.reloaded': 'ההעדפות השמורות החליפו את הטיוטה.',
  'commPrefs.unsavedIntent': 'העדפות התקשורת של המטופל',
  'commPrefs.staffUnsavedIntent': 'העדפות התקשורת שלך',
}
